import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowRight, HeartPulse, Users, BatteryLow, Feather, Facebook, Instagram, Globe, Menu, X, Ribbon, Gift } from "lucide-react";
import { CONTACT, PARCOURS, POUR_QUI, RESEAU, FAQ } from "@/data/content";
import { EXPERIENCES as TPOZ_EXPERIENCES, getThemeMoment } from "@/data/experiences";
import { DemandeDialog } from "@/components/site/DemandeBon";
import { ContactButton, EmailWithCopy } from "@/components/site/ContactFormDialog";
import { Marquee } from "@/components/site/Marquee";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const REF_IMAGES = {
  hero: "https://static.prod-images.emergentagent.com/jobs/8d3c58e4-ade2-4b9c-90d5-8cf4788b828a/images/52fc99a0943aaa94db29b3a312eced285b6634abf861ab8d0f7e8a32125270f7.jpeg",
  hebergement: "https://customer-assets-4nw71qhi.emergentagent.net/job_tpoz-wellnes/artifacts/vb35gd6h_IMG_2189.jpg",
  bienEtre: "https://static.prod-images.emergentagent.com/jobs/8d3c58e4-ade2-4b9c-90d5-8cf4788b828a/images/c5b12f9f85d767b040324dcdb96d29ca168c9a499c26811092e5c6e22f6e39e1.jpeg",
  combo: "https://static.prod-images.emergentagent.com/jobs/8d3c58e4-ade2-4b9c-90d5-8cf4788b828a/images/52fc99a0943aaa94db29b3a312eced285b6634abf861ab8d0f7e8a32125270f7.jpeg",
  finale: "https://static.prod-images.emergentagent.com/jobs/8d3c58e4-ade2-4b9c-90d5-8cf4788b828a/images/36a3dd3afccc4a4333938a4617d6323c41706e085f113825ba78bc8512269aac.jpeg",
};

const EXPERIENCES = [
  {
    key: "souffler",
    label: "Souffler, me reposer",
    img: REF_IMAGES.hebergement,
    body: "Même quelques heures : un massage, une journée spa, deux nuits en pleine nature ou face à la mer.",
  },
  {
    key: "prendre-soin",
    label: "Prendre soin de moi en douceur",
    img: REF_IMAGES.bienEtre,
    body: "Une pause sophrologie, un soin intuitif suivi d'un thé, un soin pour retrouver son équilibre.",
  },
  {
    key: "activite",
    label: "Faire une activité qui me plaît",
    img: REF_IMAGES.combo,
    body: "Cuisiner et écrire le temps d'un atelier, découvrir le neurofeedback.",
  },
  {
    key: "partager",
    label: "Partager un moment avec d'autres femmes",
    img: REF_IMAGES.finale,
    body: "Respirer ensemble au bord de mer, un atelier en session ouverte.",
  },
];

const QUI_ICONS = [HeartPulse, Users, BatteryLow, Feather];

const SectionHead = ({ n, label, dark = false }) => (
  <motion.div {...fade} className="flex items-center gap-4">
    <span className={`font-serif text-xl italic ${dark ? "text-[#D9A05B]" : "text-[#A66E08]"}`}>{n}</span>
    <span className={`h-px w-10 ${dark ? "bg-[#D9A05B]/50" : "bg-[#A66E08]/50"}`} />
    <span className={`text-xs font-semibold uppercase tracking-[0.25em] ${dark ? "text-[#D9A05B]" : "text-[#A66E08]"}`}>
      {label}
    </span>
  </motion.div>
);

export default function LandingLite() {
  const [demandeOpen, setDemandeOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const themeMoment = getThemeMoment();
  const enCeMoment = themeMoment
    ? themeMoment.experiences.map((id) => TPOZ_EXPERIENCES.find((e) => e.id === id)).filter(Boolean)
    : [];
  const MomentIcon = themeMoment?.badgeIcon === "gift" ? Gift : Ribbon;

  return (
    <div data-testid="lite-page" className="min-h-screen bg-[#FBF7EF] text-[#2E2A22]" style={{ fontFamily: "Manrope, sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#FBF7EF] border-b border-[#2E2A22]/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <img
            src="/logo-tpoz.png"
            alt="T'POZ — Répit & Ressourcement"
            data-testid="lite-brand-logo"
            className="h-14 sm:h-24 w-auto"
          />
          <nav className="flex items-center gap-2 sm:gap-4 md:gap-5">
            <Link to="/tpoz" data-testid="lite-nav-tpoz" className="hidden md:block text-sm font-semibold text-[#A66E08] hover:text-[#C6870F] transition-colors">
              Les T'POZ
            </Link>
            <a href="#apropos" data-testid="lite-nav-apropos" className="hidden md:block text-sm font-medium text-[#2E2A22] hover:text-[#A66E08] transition-colors">
              À propos
            </a>
            <a href="#partenaires" data-testid="lite-nav-partenaires" className="hidden md:block text-sm font-medium text-[#2E2A22] hover:text-[#A66E08] transition-colors">
              Partenaires
            </a>
            <a href="#faq" data-testid="lite-nav-faq" className="hidden md:block text-sm font-medium text-[#2E2A22] hover:text-[#A66E08] transition-colors">
              FAQ
            </a>
            <Link to="/offrir" data-testid="lite-nav-offrir" className="hidden md:block text-sm font-medium text-[#2E2A22] hover:text-[#A66E08] transition-colors">
              Offrir
            </Link>
            <Link
              to="/tpoz"
              data-testid="lite-header-cta"
              className="px-3 sm:px-5 py-2 sm:py-2.5 rounded-full bg-[#C6870F] text-white text-xs sm:text-sm font-semibold hover:bg-[#A66E08] transition-colors"
            >
              Demander ma T'POZ
            </Link>
            <img
              src="/logo-departement.png"
              alt="Le Département Guadeloupe"
              data-testid="lite-logo-departement"
              className="block h-9 sm:h-12 md:h-14 w-auto"
            />
            <button
              onClick={() => setMenuOpen((o) => !o)}
              data-testid="lite-mobile-menu-btn"
              aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
              className="md:hidden p-1.5 sm:p-2 rounded-full text-[#2E2A22] hover:bg-[#2E2A22]/5 transition-colors shrink-0"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </nav>
        </div>
        {menuOpen && (
          <div data-testid="lite-mobile-menu" className="md:hidden border-t border-[#2E2A22]/10 bg-[#FBF7EF] px-6 py-4 flex flex-col gap-1">
            <Link to="/tpoz" data-testid="lite-mobile-nav-tpoz" onClick={() => setMenuOpen(false)} className="py-2.5 text-sm font-semibold text-[#A66E08]">
              Les T'POZ
            </Link>
            <a href="#apropos" data-testid="lite-mobile-nav-apropos" onClick={() => setMenuOpen(false)} className="py-2.5 text-sm font-medium text-[#2E2A22] border-t border-[#2E2A22]/5">
              À propos
            </a>
            <a href="#partenaires" data-testid="lite-mobile-nav-partenaires" onClick={() => setMenuOpen(false)} className="py-2.5 text-sm font-medium text-[#2E2A22] border-t border-[#2E2A22]/5">
              Partenaires
            </a>
            <a href="#faq" data-testid="lite-mobile-nav-faq" onClick={() => setMenuOpen(false)} className="py-2.5 text-sm font-medium text-[#2E2A22] border-t border-[#2E2A22]/5">
              FAQ
            </a>
            <Link to="/offrir" data-testid="lite-mobile-nav-offrir" onClick={() => setMenuOpen(false)} className="py-2.5 text-sm font-medium text-[#2E2A22] border-t border-[#2E2A22]/5">
              Offrir
            </Link>
          </div>
        )}
      </header>

      {/* Hero plein écran */}
      <section className="relative min-h-[88vh] flex items-center">
        <img
          src={REF_IMAGES.hero}
          alt="Femme se détendant sur un transat au bord d'une piscine face à la mer"
          data-testid="lite-hero-image"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2E2A22]/80 via-[#2E2A22]/50 to-[#2E2A22]/20" />
        <div className="relative max-w-6xl mx-auto px-6 py-24 w-full">
          <motion.p {...fade} className="text-xs font-semibold uppercase tracking-[0.3em] text-[#FBF7EF]/90">
            Une initiative solidaire de Plus Belle La Vie 971 · Guadeloupe
          </motion.p>
          <motion.h1
            {...fade}
            transition={{ ...fade.transition, delay: 0.1 }}
            data-testid="lite-hero-title"
            className="mt-8 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-[#FBF7EF] max-w-3xl"
          >
            Des temps de répit
            <br />
            et de <em className="text-[#EBD9AE]">ressourcement</em>
            <br />
            pour les femmes
          </motion.h1>
          <motion.p
            {...fade}
            transition={{ ...fade.transition, delay: 0.15 }}
            data-testid="lite-hero-intro"
            className="mt-6 max-w-2xl text-base leading-relaxed text-[#FBF7EF]/90"
          >
            Vous traversez une période de vie exigeante ? En Guadeloupe, T'POZ vous propose des
            temps de répit et de ressourcement pour souffler, vous retrouver et reprendre des forces.
            <span data-testid="lite-hero-public" className="mt-3 block text-[#FBF7EF]/90">
              Pour les femmes à partir de 40 ans, et toutes celles qui en ressentent le besoin.
            </span>
          </motion.p>
          <motion.p {...fade} transition={{ ...fade.transition, delay: 0.2 }} data-testid="lite-hero-creole" className="mt-6 font-serif text-xl italic text-[#FBF7EF]/85">
            « Pran on ti poz, pozé kò aw. »
          </motion.p>
          <motion.div {...fade} transition={{ ...fade.transition, delay: 0.3 }} className="mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 w-full max-w-sm sm:max-w-none">
            <button
              onClick={() => document.getElementById("experiences")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="lite-cta-code"
              className="w-full sm:w-auto px-7 py-3.5 rounded-full bg-[#FBF7EF] text-[#2E2A22] text-sm font-semibold hover:bg-[#EBD9AE] transition-colors text-center"
            >
              Choisir ma T'POZ
            </button>
            <Link
              to="/offrir"
              data-testid="lite-cta-offrir"
              className="w-full sm:w-auto px-7 py-3.5 rounded-full border border-[#FBF7EF]/50 text-[#FBF7EF] text-sm font-semibold hover:bg-[#FBF7EF]/10 transition-colors text-center"
            >
              Offrir une T'POZ
            </Link>
          </motion.div>
          <motion.p
            {...fade}
            transition={{ ...fade.transition, delay: 0.4 }}
            className="mt-5 w-full max-w-sm text-center sm:max-w-none sm:text-left"
          >
            <a
              href="#structures"
              data-testid="lite-cta-structure"
              className="inline-flex items-center gap-1.5 text-sm text-[#FBF7EF]/85 underline underline-offset-4 hover:text-[#EBD9AE] transition-colors"
            >
              Vous êtes une structure ? →
            </a>
          </motion.p>
        </div>
      </section>

      {/* Marquee valeurs */}
      <Marquee />

      {/* En ce moment */}
      {themeMoment && enCeMoment.length > 0 && (
      <section className="max-w-6xl mx-auto px-6 pt-10 md:pt-12" data-testid="lite-en-ce-moment">
        <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5" style={{ backgroundColor: `${themeMoment.couleur}1F` }} data-testid="lite-moment-theme-badge">
          <MomentIcon className="h-4 w-4" strokeWidth={2} style={{ color: themeMoment.couleur }} />
          <span className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: themeMoment.couleur }}>{themeMoment.surtitre}</span>
        </div>
        <h2 className="mt-4 font-serif text-3xl sm:text-4xl">
          En ce moment : <em style={{ color: themeMoment.couleur }}>{themeMoment.titreEm}</em>
        </h2>
        <p className="mt-3 max-w-2xl text-sm text-[#6B6355]">
          {themeMoment.texte}
        </p>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {enCeMoment.map((e) => (
            <Link
              key={e.id}
              to={`/tpoz?exp=${e.id}`}
              data-testid={`lite-moment-${e.id}`}
              className="group block overflow-hidden rounded-2xl border border-[#2E2A22]/8 bg-white transition-all hover:-translate-y-1 hover:shadow-[0_12px_30px_rgba(45,40,48,0.1)]"
            >
              <div className="relative overflow-hidden">
                <img src={e.photo} alt={e.nom} loading="lazy" className="w-full aspect-[4/3] object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                <span data-testid={`lite-moment-badge-${e.id}`} className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm" style={{ backgroundColor: themeMoment.couleur }}>
                  <MomentIcon className="h-3 w-3" strokeWidth={2.2} /> {themeMoment.badge}
                </span>
              </div>
              <div className="p-5">
                <p className="font-serif text-lg leading-snug">{e.nom}</p>
                <p className="mt-1 text-xs text-[#6B6355]">{e.dureeLabel} · {e.lieuxLabel}</p>
                <p className="mt-1 text-xs font-semibold" style={{ color: themeMoment.couleur }}>{themeMoment.periodeCartes}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
      )}

      {/* 01 — Pourquoi T'POZ ? */}
      <section id="apropos" className="max-w-6xl mx-auto px-6 py-12 md:py-16 scroll-mt-28">
        <SectionHead n="01" label="Pourquoi T'POZ ?" />
        <div className="mt-6 grid md:grid-cols-[1.4fr_1fr] gap-8 items-start">
          <motion.h2 {...fade} className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.15]">
            Parce qu'il existe des périodes de vie où l'on a besoin de{" "}
            <em className="text-[#A66E08]">souffler</em>, de se retrouver, de{" "}
            <em className="text-[#A66E08]">reprendre des forces</em>.
          </motion.h2>
          <motion.div {...fade} className="text-sm text-[#6B6355] leading-relaxed space-y-4 md:pt-3">
            <p>
              T'POZ offre des bulles de répit et de ressourcement aux femmes à partir de 40 ans qui
              traversent une période de vie exigeante, pour prendre soin d'elles à leur rythme. Les
              femmes plus jeunes sont aussi les bienvenues.
            </p>
            <p>Une initiative solidaire portée par l'association {CONTACT.association}.</p>
          </motion.div>
        </div>
      </section>

      {/* 02 — À qui s'adresse T'POZ ? */}
      <section className="bg-[#F1E8D8]/60">
        <div className="max-w-6xl mx-auto px-6 py-12 md:py-16">
          <SectionHead n="02" label="À qui s'adresse T'POZ ?" />
          <div className="mt-6 grid md:grid-cols-[1.4fr_1fr] gap-8 items-start">
            <motion.h2 {...fade} className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.15]">
              Pour celles qui traversent une période de vie <em className="text-[#A66E08]">exigeante</em>
            </motion.h2>
            <motion.p {...fade} className="text-sm text-[#6B6355] leading-relaxed md:pt-3">
              {POUR_QUI.intro}
            </motion.p>
          </div>
          <div className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="lite-eligibility-list">
            {POUR_QUI.femmes.map((item, i) => {
              const Icon = QUI_ICONS[i % QUI_ICONS.length];
              return (
                <motion.div
                  key={i}
                  {...fade}
                  transition={{ ...fade.transition, delay: i * 0.05 }}
                  className="flex items-center gap-3 rounded-xl bg-white border border-[#2E2A22]/5 px-4 py-3"
                >
                  <Icon className="h-4 w-4 text-[#C6870F] shrink-0" strokeWidth={1.6} />
                  <p className="text-sm leading-snug">{item}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 03 — Comment ça fonctionne ? */}
      <section className="max-w-6xl mx-auto px-6 py-12 md:py-16">
        <SectionHead n="03" label="Comment ça fonctionne ?" />
        <motion.h2 {...fade} className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.15]">
          Quatre étapes, <em className="text-[#A66E08]">tout en douceur</em>
        </motion.h2>
        <motion.p {...fade} data-testid="lite-parcours-note" className="mt-3 text-sm font-semibold text-[#A66E08]">
          Un code personnel, valable pour 3 T'POZ par an.
        </motion.p>
        <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PARCOURS.map((step, i) => (
            <motion.div
              key={step.n}
              {...fade}
              transition={{ ...fade.transition, delay: i * 0.08 }}
              data-testid={`lite-step-${step.n}`}
              className="rounded-2xl border border-[#2E2A22]/8 p-5"
            >
              <span className="font-serif text-3xl italic text-[#C6870F]/50">0{step.n}</span>
              <h3 className="mt-2 font-serif text-xl">{step.title}</h3>
              <p className="mt-1.5 text-sm text-[#6B6355] leading-normal">{step.body}</p>
            </motion.div>
          ))}
        </div>
        <motion.p {...fade} data-testid="lite-parcours-conditions" className="mt-5 text-sm italic text-[#6B6355] max-w-3xl">
          T'POZ propose 3 pauses par an. Les pauses entièrement offertes sont limitées à une par an et
          par bénéficiaire. Pour connaître les conditions des autres pauses, contactez l'association
          Plus Belle la Vie 971.
        </motion.p>
        <motion.div {...fade} data-testid="lite-parcours-aide" className="mt-4 rounded-2xl bg-white border border-[#2E2A22]/8 p-5">
          <h3 className="font-serif text-xl">Besoin d'aide pour faire votre demande ?</h3>
          <p className="mt-1.5 text-sm text-[#6B6355] leading-normal">
            Pour savoir si vous pouvez bénéficier de T'POZ et connaître les démarches, contactez
            directement l'association Plus Belle la Vie 971.
          </p>
          <p className="mt-2 text-sm font-semibold text-[#A66E08]">
            E-mail :{" "}
            <EmailWithCopy testId="lite-aide-email" />
            {" "}· Téléphone :{" "}
            <a href={CONTACT.phoneHref} className="underline underline-offset-4 hover:text-[#C6870F]" data-testid="lite-aide-phone">{CONTACT.phone}</a>
          </p>
        </motion.div>
      </section>

      {/* 04 — Expériences T'POZ */}
      <section id="experiences" className="max-w-6xl mx-auto px-6 pb-12 md:pb-16 scroll-mt-28">
        <SectionHead n="04" label="Expériences T'POZ" />
        <div className="mt-6 grid md:grid-cols-[1.4fr_1fr] gap-8 items-start">
          <motion.h2 {...fade} className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.15]">
            Des expériences qui <em className="text-[#A66E08]">donnent envie</em>
          </motion.h2>
          <motion.p {...fade} className="text-sm text-[#6B6355] leading-relaxed md:pt-3">
            Des moments choisis par T'POZ, à vivre selon ce qui vous ferait du bien.
          </motion.p>
        </div>

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {EXPERIENCES.map((e, i) => (
            <motion.div key={e.key} {...fade} transition={{ ...fade.transition, delay: i * 0.08 }} data-testid={`lite-offer-${e.key}`}>
              <Link to={`/tpoz?besoin=${e.key}`} data-testid={`lite-offer-link-${e.key}`} className="group block">
                <div className="overflow-hidden rounded-2xl">
                  <img src={e.img} alt={e.label} className="w-full h-56 object-cover rounded-2xl transition-transform duration-500 group-hover:scale-[1.04]" />
                </div>
                <h3 className="mt-4 font-serif text-xl transition-colors group-hover:text-[#A66E08]">{e.label}</h3>
                <p className="mt-2 text-sm text-[#6B6355] leading-normal">{e.body}</p>
                <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#A66E08]">
                  Voir les T'POZ <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>

        <motion.div
          {...fade}
          data-testid="lite-encart-assoc"
          className="mt-6 rounded-2xl border border-[#2E2A22]/8 bg-white p-5"
        >
          <h3 className="font-serif text-xl">Les T'POZ de l'association</h3>
          <p className="mt-1 text-sm text-[#6B6355]">À venir.</p>
        </motion.div>

        {/* Bandeau carte interactive */}
        <motion.div
          {...fade}
          data-testid="lite-carte-banner"
          className="mt-8 rounded-3xl bg-[#2E2A22] text-[#FBF7EF] px-8 py-8 md:px-12 flex flex-col md:flex-row md:items-center gap-6"
        >
          <div className="flex-1">
            <h3 className="font-serif text-2xl md:text-3xl">
              Découvrez les lieux T'POZ près de chez vous
            </h3>
            <p className="mt-2 text-sm text-[#FBF7EF]/70 leading-relaxed">
              Des temps de répit partout en Guadeloupe, sur une carte interactive avec fiches détaillées.
            </p>
          </div>
          <Link
            to="/carte"
            data-testid="lite-carte-cta"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#C6870F] text-white text-sm font-semibold hover:bg-[#D9A05B] transition-colors shrink-0"
          >
            <MapPin className="h-4 w-4" /> Explorer la carte
          </Link>
        </motion.div>
      </section>

      {/* 05 — Les partenaires (section sombre) */}
      <section id="partenaires" className="bg-[#2E2A22] text-[#FBF7EF] scroll-mt-28">
        <div className="max-w-6xl mx-auto px-6 py-12 md:py-16">
          <SectionHead n="05" label="Les partenaires" dark />
          <div className="mt-6 grid md:grid-cols-[1.4fr_1fr] gap-8 items-start">
            <motion.h2 {...fade} className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.15]">
              Un réseau de partenaires <em className="text-[#D9A05B]">engagés</em>
            </motion.h2>
            <motion.p {...fade} className="text-sm text-[#FBF7EF]/70 leading-relaxed md:pt-3">
              Que vous soyez hébergeur, professionnel du bien-être, organisme, entreprise ou
              institution, vous pouvez participer à votre manière : proposer une expérience, relayer
              le réseau ou soutenir son développement.
            </motion.p>
          </div>
          <div className="mt-8 grid sm:grid-cols-3 gap-6">
            {RESEAU.map((r) => (
              <motion.div key={r.key} {...fade} id={r.anchor} data-testid={`lite-reseau-${r.key}`} className="flex flex-col rounded-2xl border border-[#FBF7EF]/10 p-7 scroll-mt-28">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D9A05B]">Vous êtes…</p>
                <h3 className="mt-4 font-serif text-2xl leading-snug">{r.title}</h3>
                <p className="mt-3 text-sm text-[#FBF7EF]/70 leading-relaxed flex-1">{r.body}</p>
                {r.links ? (
                  <div className="mt-6 flex flex-col gap-2.5">
                    {r.links.map((lnk) => (
                      <ContactButton
                        key={lnk.key}
                        subject={lnk.subject}
                        testId={`lite-reseau-link-${r.key}-${lnk.key}`}
                        className="inline-block self-start text-sm font-semibold text-[#D9A05B] underline underline-offset-4 hover:text-[#EBD9AE] transition-colors"
                      >
                        {lnk.label} →
                      </ContactButton>
                    ))}
                  </div>
                ) : (
                  <ContactButton
                    subject={r.subject}
                    testId={`lite-reseau-cta-${r.key}`}
                    className="mt-6 inline-block self-start text-sm font-semibold text-[#D9A05B] underline underline-offset-4 hover:text-[#EBD9AE] transition-colors"
                  >
                    {r.cta} →
                  </ContactButton>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 06 — FAQ */}
      <section id="faq" className="bg-[#F1E8D8]/60 scroll-mt-28">
        <div className="max-w-6xl mx-auto px-6 py-12 md:py-16 grid md:grid-cols-[1fr_1.4fr] gap-10">
          <div>
            <SectionHead n="06" label="Questions fréquentes" />
            <motion.h2 {...fade} className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.15]">
              Vous vous <em className="text-[#A66E08]">demandez</em> peut-être…
            </motion.h2>
          </div>
          <motion.div {...fade}>
            <Accordion type="single" collapsible className="w-full" data-testid="lite-faq-accordion">
              {FAQ.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-[#2E2A22]/10">
                  <AccordionTrigger data-testid={`lite-faq-trigger-${i}`} className="font-serif text-lg font-normal text-left hover:no-underline hover:text-[#A66E08] py-3.5">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-[#6B6355] leading-relaxed">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* 07 — Accompagnée par une structure */}
      <section id="relais" className="max-w-6xl mx-auto px-6 py-12 scroll-mt-28">
        <SectionHead n="07" label="Structures et accompagnement" />
        <motion.h2 {...fade} className="mt-6 font-serif text-3xl sm:text-4xl leading-[1.15]">
          Vous êtes accompagnée par une <em className="text-[#A66E08]">structure</em> ?
        </motion.h2>
        <motion.div {...fade} data-testid="lite-relais-list" className="mt-6 rounded-2xl border border-dashed border-[#A66E08]/40 bg-white/60 p-6">
          <p className="text-sm text-[#6B6355] leading-relaxed max-w-2xl">
            Elle peut vous orienter vers T'POZ et vous aider à faire votre demande.
          </p>
          <p className="mt-4 text-sm text-[#6B6355] leading-relaxed">
            Pour toute question, contactez l'association {CONTACT.association} :{" "}
            <EmailWithCopy testId="lite-relais-email" className="font-semibold text-[#A66E08]" />{" "}
            ·{" "}
            <a href={CONTACT.phoneHref} className="font-semibold text-[#A66E08] underline underline-offset-4 hover:text-[#C6870F]" data-testid="lite-relais-phone">
              {CONTACT.phone}
            </a>
          </p>
        </motion.div>
      </section>

      {/* CTA finale */}
      <section className="relative min-h-[55vh] flex items-center">
        <img
          src={REF_IMAGES.finale}
          alt="Femme se ressourçant dans la nature"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#2E2A22]/55" />
        <div className="relative max-w-6xl mx-auto px-6 py-14 w-full text-center">
          <motion.p {...fade} className="text-xs font-semibold uppercase tracking-[0.3em] text-[#EBD9AE]">
            Prête à souffler ?
          </motion.p>
          <motion.h2 {...fade} data-testid="lite-final-title" className="mt-6 font-serif text-4xl sm:text-5xl text-[#FBF7EF]">
            Offrez-vous un temps de <em className="text-[#EBD9AE]">répit</em>
          </motion.h2>
          <motion.p {...fade} className="mt-4 font-serif text-lg italic text-[#FBF7EF]/80">
            Poze kò aw — nou la pou vou.
          </motion.p>
          <motion.div {...fade} className="mt-10 flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setDemandeOpen(true)}
              data-testid="lite-final-cta-demande"
              className="px-7 py-3.5 rounded-full bg-[#FBF7EF] text-[#2E2A22] text-sm font-semibold hover:bg-[#EBD9AE] transition-colors"
            >
              Demander ma T'POZ
            </button>
            <Link
              to="/offrir"
              data-testid="lite-final-cta-offrir"
              className="px-7 py-3.5 rounded-full border border-[#FBF7EF]/50 text-[#FBF7EF] text-sm font-semibold hover:bg-[#FBF7EF]/10 transition-colors"
            >
              Offrir une T'POZ
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2E2A22] text-[#FBF7EF] border-t border-[#FBF7EF]/10">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div className="grid md:grid-cols-2 gap-10">
            <div>
              <h2 className="font-serif text-2xl" data-testid="lite-footer-title">T'POZ</h2>
              <p className="mt-5 text-sm text-[#FBF7EF]/70 leading-relaxed max-w-md" data-testid="lite-footer-desc">
                Des temps de répit et de ressourcement pour les femmes, en Guadeloupe. T'POZ est une
                initiative solidaire de l'association Plus Belle La Vie 971.
              </p>
              <p className="mt-4 font-serif italic text-lg text-[#D9A05B]" data-testid="lite-footer-creole">
                « Pou tout fanm doubout an Gwadloup. »
              </p>
              <p className="mt-4 text-sm text-[#FBF7EF]/80" data-testid="lite-footer-appel">
                Vous préférez appeler ?{" "}
                <a href={CONTACT.phoneHref} className="font-semibold text-[#EBD9AE] underline underline-offset-4 hover:text-[#D9A05B] transition-colors">
                  {CONTACT.phone}
                </a>
              </p>
            </div>
            <div className="md:text-right">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#FBF7EF]/50">
                Suivre l'association :
              </p>
              <div className="mt-3 flex md:justify-end items-center gap-4">
                <a
                  href="https://www.facebook.com/plusbellelavie97"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Facebook Plus Belle La Vie 971"
                  data-testid="lite-footer-facebook"
                  className="text-[#D9A05B] hover:text-[#EBD9AE] transition-colors"
                >
                  <Facebook className="h-5 w-5" strokeWidth={1.8} />
                </a>
                <a
                  href="https://www.instagram.com/plusbellelavie971"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Instagram Plus Belle La Vie 971"
                  data-testid="lite-footer-instagram"
                  className="text-[#D9A05B] hover:text-[#EBD9AE] transition-colors"
                >
                  <Instagram className="h-5 w-5" strokeWidth={1.8} />
                </a>
                <a
                  href="https://www.plusbellelavie971.com"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Site de l'association"
                  data-testid="lite-footer-assoc-link"
                  className="text-[#D9A05B] hover:text-[#EBD9AE] transition-colors"
                >
                  <Globe className="h-5 w-5" strokeWidth={1.8} />
                </a>
              </div>
              <div className="mt-8 flex flex-wrap md:justify-end gap-x-6 gap-y-2 text-sm text-[#FBF7EF]/60">
                <Link to="/carte" className="hover:text-[#D9A05B] transition-colors" data-testid="lite-footer-carte">
                  La carte des lieux
                </Link>
                <Link to="/espace" className="hover:text-[#D9A05B] transition-colors" data-testid="lite-footer-espace">
                  Espace bénéficiaire
                </Link>
                <Link to="/presse" className="hover:text-[#D9A05B] transition-colors" data-testid="lite-footer-presse">
                  Espace presse
                </Link>
              </div>
              <div className="mt-8 flex md:justify-end">
                <a
                  href="https://www.plusbellelavie971.com"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Association Plus Belle La Vie 971"
                  data-testid="lite-footer-logo-pblv"
                >
                  <img
                    src="/logo-pblv971.png"
                    alt="Logo association Plus Belle La Vie 971"
                    loading="lazy"
                    className="h-32 w-32 rounded-2xl bg-white object-contain p-1.5 shadow-[0_4px_18px_rgba(0,0,0,0.25)]"
                  />
                </a>
              </div>
            </div>
          </div>
          <p className="mt-8 pt-6 border-t border-[#FBF7EF]/10 text-xs text-[#FBF7EF]/40" data-testid="lite-footer-copyright">
            © 2026 T'POZ · Association Plus Belle La Vie 971 · Guadeloupe ·{" "}
            <Link to="/mentions-legales" className="hover:text-[#D9A05B] transition-colors underline underline-offset-2" data-testid="lite-footer-mentions">
              Mentions légales &amp; confidentialité
            </Link>{" "}
            ·{" "}
            <Link to="/conditions" className="hover:text-[#D9A05B] transition-colors underline underline-offset-2" data-testid="lite-footer-conditions">
              Conditions d'utilisation
            </Link>{" "}
            ·{" "}
            <Link to="/admin/login" className="hover:text-[#D9A05B] transition-colors" data-testid="lite-footer-admin">
              Espace association
            </Link>
          </p>
        </div>
      </footer>

      <DemandeDialog open={demandeOpen} onOpenChange={setDemandeOpen} />
    </div>
  );
}
